import React, { useEffect, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";

import { DateSelectArg, EventApi, EventClickArg, EventContentArg, EventInput, EventMountArg } from "@fullcalendar/core";
import { Lock } from "lucide-react";
import { decodeErrorResponse } from "@/utils/errors";
import { errorPopup, successPopup } from "@/utils/alerts";
import { reasonLabel } from "@/utils/blocks";
import CalendarEventDialog from "@/lib/shadcn/components/calendar-event-modal";
import { Card } from "@/lib/shadcn/ui/card";
import { useActiveApartmentEntities, useActiveApartmentOptions } from "@/store/slices/apartments";
import { fetchOptions } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import { useAppData } from "@/hooks/useAppData";
import SearchableSelect from "@/lib/shadcn/components/searchable-select";
import type { StatFilter } from "@/hooks/useAggregatedStats";
import type { Apartment } from "@/models/responses";

const calendarStyles = `
  :root {
    --fc-small-font-size: 0.85em;
    --fc-page-bg-color: var(--background);
    --fc-border-color: hsl(214.3 31.8% 91.4%);
    --fc-button-text-color: hsl(222.2 47.4% 11.2%);
    --fc-button-bg-color: transparent;
    --fc-button-border-color: hsl(214.3 31.8% 91.4%);
    --fc-button-hover-bg-color: hsl(210 40% 96.1%);
    --fc-button-hover-border-color: hsl(214.3 31.8% 91.4%);
    --fc-button-active-bg-color: hsl(210 40% 96.1%);
    --fc-button-active-border-color: hsl(214.3 31.8% 91.4%);
    --fc-event-bg-color: hsl(221.2 83.2% 53.3%);
    --fc-event-border-color: hsl(221.2 83.2% 53.3%);
    --fc-event-text-color: #fff;
    --fc-event-selected-overlay-color: rgba(0, 0, 0, 0.25);
    --fc-today-bg-color: hsl(210 40% 98%);
    --fc-neutral-bg-color: #fff;
  }

  .fc {
    font-family: var(--font-sans);
  }

  .fc .fc-toolbar.fc-header-toolbar {
    margin-bottom: 1.5em;
  }

  .fc .fc-button {
    display: inline-flex;
    height: 2.5rem;
    items-center;
    justify-content: center;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    font-weight: 500;
    padding: 0.5rem 1rem;
    transition-property: color, background-color, border-color;
    transition-duration: 150ms;
  }

  .fc .fc-button:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--background), 0 0 0 4px hsl(215.4 16.3% 46.9%);
  }

  .fc .fc-button-primary:not(:disabled):active,
  .fc .fc-button-primary:not(:disabled).fc-button-active {
    background-color: hsl(217.2 91.2% 59.8%);
    border-color: hsl(217.2 91.2% 59.8%);
    color: #fff;
  }

  .fc .fc-toolbar-title {
    font-size: 1.25rem;
    line-height: 1.75rem;
    font-weight: 600;
  }

  .fc .fc-view-harness {
    background-color: #fff;
    border-radius: 0.5rem;
    border: 1px solid hsl(214.3 31.8% 91.4%);
    box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  }

  .fc .fc-list-sticky .fc-list-day > * {
    background-color: hsl(210 40% 98%);
  }

  .fc .fc-list-event:hover td {
    background-color: hsl(210 40% 96.1%);
  }

  .fc-event {
    border-radius: 0.375rem;
    cursor: pointer;
  }

  .fc-event-main {
    padding: 0.25rem;
  }

  .fc-theme-standard td, .fc-theme-standard th {
    border-color: hsl(214.3 31.8% 91.4%);
  }

  .fc .fc-day-today {
    background-color: hsl(210 40% 98%) !important;
  }

  /* HostHub-sourced (read-only) blocks: a distinct navy/slate, never the
     manual blue. The distinction is ALSO carried by an in-event lock icon +
     reason text (see renderEventContent) so it is never colour-only. */
  .fc-event.hosthub-block {
    background-color: hsl(215 25% 27%);
    border-color: hsl(215 25% 27%);
    cursor: default;
  }
`;

const BlockedCalendarPage: React.FC = () => {
    const apartmentOptions = useActiveApartmentOptions();
    const apartmentEntities = useActiveApartmentEntities();

    const [showModal, setShowModal] = useState(false);
    // True when the open dialog shows a HostHub-sourced block: the dialog is
    // read-only and the mutation handlers (delete/update) are never wired for
    // it. Set BEFORE setShowModal(true) in handleEventClick.
    const [readOnly, setReadOnly] = useState(false);
    const [selectedStartDate, setSelectedStartDate] = useState("");
    const [selectedEndDate, setSelectedEndDate] = useState("");
    const [currentEvent, setCurrentEvent] = useState<EventApi | null>(null);
    const calendarRef = useRef<FullCalendar>(null);

    const { predicates } = useApartmentFilters();
    const { groupOptions } = useAppData();

    // Page-local Apartment + Apartment-Group filters (0 = All). Deliberately
    // page-local rather than the global userSettings selection: userSettings
    // auto-selects the first apartment once apartments load, which would wrongly
    // default this calendar to a single apartment instead of showing every block.
    const [apartmentFilter, setApartmentFilter] = useState(0);
    const [groupFilter, setGroupFilter] = useState(0);

    /**
     * `eventSources` is a static config passed to FullCalendar; its callback
     * closes over its initial values. To keep filtering reactive without
     * tearing down the calendar on every predicate change, we stash the
     * latest predicates and entities in refs. The callback reads via the
     * refs, and a separate effect calls `refetchEvents()` whenever the
     * predicates or entities change so the visible viewport re-paints.
     */
    const predicatesRef = useRef<StatFilter[]>(predicates);
    const apartmentEntitiesRef = useRef<Record<number, Apartment>>(apartmentEntities);
    const apartmentFilterRef = useRef<number>(apartmentFilter);
    const groupFilterRef = useRef<number>(groupFilter);
    useEffect(() => {
        predicatesRef.current = predicates;
        apartmentEntitiesRef.current = apartmentEntities;
        apartmentFilterRef.current = apartmentFilter;
        groupFilterRef.current = groupFilter;
        const calendarApi = calendarRef.current?.getApi();
        calendarApi?.refetchEvents();
    }, [predicates, apartmentEntities, apartmentFilter, groupFilter]);

    const handleCloseModal = () => {
        setCurrentEvent(null);
        setReadOnly(false);
        setShowModal(false);
    };

    const handleEventDeleted = () => {
        fetch(`${apiUrl}/api/admin/apartments/blocks/${currentEvent?.id}`, {
            method: "DELETE",
            credentials: "include",
        })
            .then((response) => {
                if (!response) {
                    throw new Error("No response received from the server.");
                }
                if (!response.ok) {
                    // if HTTP-status is 404, 500, etc
                    throw new Error("" + response.status);
                }
                currentEvent?.remove();
                setCurrentEvent(null);
                setShowModal(false);
                successPopup("Event deleted successfully.");
            })
            .catch((error) => {
                void errorPopup(error.message);
            });
    };

    function handleNewEventAdded(apartmentId: number, start: string, end: string, notes: string) {
        fetch(`${apiUrl}/api/admin/apartments/blocks`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                apartmentId,
                start,
                end,
                notes,
            }),
            credentials: "include",
        })
            .then(async (response) => {
                if (!response) {
                    throw new Error("No response received from the server.");
                }
                if (!response.ok) {
                    // if HTTP-status is 404, 500, etc
                    throw await decodeErrorResponse(response);
                }
                return response.json();
            })
            .then((data) => {
                const newEvent = {
                    id: data.id,
                    title: data.apartment,
                    start: data.start,
                    end: data.end,
                    extendedProps: {
                        apartmentId: data.apartmentId,
                        notes: data.notes,
                    },
                };
                let calendarApi = calendarRef.current;
                if (calendarApi) {
                    calendarApi.getApi().addEvent(newEvent);
                }
                setShowModal(false);
                successPopup("Event added successfully.");
            })
            .catch((error) => {
                void errorPopup("An error occurred while adding the event: " + error.message);
            });
    }

    function handleEventUpdated(eventId: string, apartmentId: number, start: string, end: string, notes: string) {
        fetch(`${apiUrl}/api/admin/apartments/blocks/${eventId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                apartmentId,
                start,
                end,
                notes,
            }),
            credentials: "include",
        })
            .then(async function (response) {
                if (!response) {
                    throw new Error("No response received from the server.");
                }
                if (!response.ok) {
                    // if HTTP-status is 404, 500, etc
                    throw await decodeErrorResponse(response);
                }
                return response.json();
            })
            .then((data) => {
                const updatedEvent = {
                    id: data.id,
                    title: data.apartment,
                    start: data.start,
                    end: data.end,
                    extendedProps: {
                        apartmentId: data.apartmentId,
                        notes: data.notes,
                    },
                };
                let calendarApi = calendarRef.current;
                if (calendarApi) {
                    let existingEvent = calendarApi.getApi().getEventById(eventId);
                    if (existingEvent) {
                        existingEvent.remove();
                        calendarApi.getApi().addEvent(updatedEvent);
                    }
                }
                setShowModal(false);
                successPopup("Event updated successfully.");
            })
            .catch((error) => {
                void errorPopup("An error occurred while updating the event: " + error.message);
            });
    }

    const handleDateClick = (clickInfo: DateClickArg) => {
        setCurrentEvent(null);
        setSelectedStartDate(clickInfo.dateStr);
        let endDate = new Date(clickInfo.dateStr);
        endDate.setDate(endDate.getDate() + 1);
        let endDateStr = endDate.toISOString().split("T")[0];
        setSelectedEndDate(endDateStr);
        setShowModal(true);
    };

    const handleEventClick = (clickInfo: EventClickArg) => {
        setCurrentEvent(clickInfo.event);
        setSelectedStartDate(clickInfo.event.startStr);

        // HostHub blocks are read-only — flip the flag BEFORE opening the
        // dialog so the mutation handlers (handleEventDeleted /
        // handleEventUpdated) are never reachable for them.
        const origin = clickInfo.event.extendedProps?.origin as string | undefined;
        setReadOnly(origin === "hosthub");

        setShowModal(true);
    };

    const handleDateSelect = (selectInfo: DateSelectArg) => {
        setCurrentEvent(null);
        let calendarApi = selectInfo.view.calendar;
        calendarApi.unselect(); // clear date selection
        setSelectedStartDate(selectInfo.startStr);
        setSelectedEndDate(selectInfo.endStr);
        setShowModal(true);
    };

    function renderEventContent(eventContent: EventContentArg) {
        const origin = eventContent.event.extendedProps?.origin as string | undefined;
        const reason = (eventContent.event.extendedProps?.reason as string | undefined) ?? "";
        // TODO(hosthub-block-sync): amber conflict marker on manual events that
        // overlap a HostHub block (deferred — the daily digest is the
        // authoritative conflict surface; this would be a secondary hint only).
        if (origin === "hosthub") {
            return (
                <div className="flex flex-col gap-0.5 p-1">
                    <div className="flex items-center gap-1 text-xs font-medium text-white">
                        <Lock data-testid="hosthub-lock-icon" aria-hidden="true" className="h-3 w-3 shrink-0" />
                        <span className="truncate">{reasonLabel(reason) || "HostHub"}</span>
                    </div>
                    <div className="text-sm truncate text-white">{eventContent.event.title}</div>
                </div>
            );
        }
        return (
            <div className="flex flex-col gap-0.5 p-1">
                <div className="text-xs font-medium text-white">{eventContent.timeText}</div>
                <div className="text-sm truncate text-white">{eventContent.event.title}</div>
            </div>
        );
    }

    // Stamp the rendered .fc-event root with the logical block id (for
    // tests + DOM targeting) and, for HostHub blocks, the distinct
    // `hosthub-block` colour class. Per-event `editable:false` (set in the
    // event source) already strips FullCalendar's drag/resize affordances.
    function handleEventDidMount(info: EventMountArg) {
        info.el.dataset.blockId = info.event.id;
        if (info.event.extendedProps?.origin === "hosthub") {
            info.el.classList.add("hosthub-block");
        }
    }

    return (
        <>
            <style>{calendarStyles}</style>
            <Card className="p-6 mt-4" data-testid="blocked-page">
                <div className="mb-4 flex flex-col lg:flex-row gap-2">
                    <ApartmentFilterBar />
                    <div data-testid="blocked-filter-group" role="group" aria-label="Apartment Group filter">
                        <SearchableSelect
                            variant="volt"
                            selectedValue={groupFilter.toString()}
                            onChange={(value) => setGroupFilter(Number(value))}
                            placeholder="Select Group"
                            options={groupOptions}
                            selectedValuePrefix="Group"
                            width="w-auto"
                        />
                    </div>
                    <div data-testid="blocked-filter-apartment" role="group" aria-label="Apartment filter">
                        <SearchableSelect
                            variant="volt"
                            selectedValue={apartmentFilter.toString()}
                            onChange={(value) => setApartmentFilter(Number(value))}
                            placeholder="Select Apartment"
                            options={apartmentOptions}
                            selectedValuePrefix="Apartment"
                            width="w-auto"
                        />
                    </div>
                </div>
                <div data-testid="blocked-calendar">
                    <FullCalendar
                        ref={calendarRef}
                        plugins={[listPlugin, dayGridPlugin, timeGridPlugin, interactionPlugin]}
                        headerToolbar={{
                            left: "prev,next",
                            center: "title",
                            right: "listWeek,dayGridMonth",
                        }}
                        initialView="dayGridMonth"
                        buttonText={{
                            prev: "Previous",
                            next: "Next",
                            listWeek: "List",
                            dayGridMonth: "Month",
                        }}
                        editable={true}
                        selectable={true}
                        selectMirror={true}
                        dayMaxEvents={true}
                        weekends={true}
                        select={handleDateSelect}
                        eventContent={renderEventContent}
                        eventDidMount={handleEventDidMount}
                        eventClick={handleEventClick}
                        dateClick={handleDateClick}
                        eventSources={[
                            function (fetchInfo, successCallback, failureCallback) {
                                fetch(
                                    `${apiUrl}/api/admin/apartments/blocks?startDate=${fetchInfo.startStr}&endDate=${fetchInfo.endStr}`,
                                    fetchOptions,
                                )
                                    .then((response) => {
                                        if (response) {
                                            return response.json();
                                        }
                                        throw new Error("Unauthorized");
                                    })
                                    .then((events) => {
                                        if (!events) {
                                            throw new Error("No response received from the server.");
                                        }
                                        // Apply the shared apartment-filter
                                        // predicates (read via ref so this
                                        // closure stays in sync with the
                                        // latest filter state). Each event's
                                        // top-level `apartmentId`
                                        // resolves to the apartment record;
                                        // events whose apartment is unknown
                                        // are excluded under any active
                                        // filter, matching Tier 1 row
                                        // semantics.
                                        const preds = predicatesRef.current;
                                        const entities = apartmentEntitiesRef.current;
                                        const aptFilter = apartmentFilterRef.current;
                                        const grpFilter = groupFilterRef.current;
                                        const anyFilterActive =
                                            preds.length > 0 || aptFilter !== 0 || grpFilter !== 0;
                                        const filtered = !anyFilterActive
                                            ? events
                                            : events.filter((event: { apartmentId?: number }) => {
                                                  // apartmentId is a TOP-LEVEL field
                                                  // (ApartmentBlockCalendarElement); this filter runs on
                                                  // the raw response BEFORE FullCalendar folds custom
                                                  // keys into extendedProps, so read it from the top level.
                                                  const apartmentId = event.apartmentId;
                                                  if (apartmentId === undefined) {
                                                      return false;
                                                  }
                                                  const apartment = entities[apartmentId];
                                                  if (!apartment) {
                                                      return false;
                                                  }
                                                  // Apartment + Apartment-Group selectors (0 = All).
                                                  if (aptFilter !== 0 && apartment.id !== aptFilter) {
                                                      return false;
                                                  }
                                                  if (grpFilter !== 0 && apartment.groupId !== grpFilter) {
                                                      return false;
                                                  }
                                                  return preds.every((predicate) => predicate(apartment));
                                              });
                                        // HostHub blocks are read-only: set
                                        // per-event editable:false so they are
                                        // non-draggable / non-resizable (the
                                        // global editable={true} stays for
                                        // manual blocks). origin/reason ride
                                        // along as top-level props which
                                        // FullCalendar folds into extendedProps.
                                        const mapped: EventInput[] = (filtered as EventInput[]).map((event) => {
                                            const origin = (event as { origin?: string }).origin;
                                            return origin === "hosthub" ? { ...event, editable: false } : event;
                                        });
                                        successCallback(mapped);
                                    })
                                    .catch((error) => {
                                        failureCallback(error);
                                    });
                            },
                        ]}
                    />
                </div>
                <CalendarEventDialog
                    open={showModal}
                    onClose={handleCloseModal}
                    onDelete={handleEventDeleted}
                    onUpdate={handleEventUpdated}
                    onAddNew={handleNewEventAdded}
                    dateStart={selectedStartDate}
                    dateEnd={selectedEndDate}
                    existingEvent={currentEvent}
                    readOnly={readOnly}
                    apartmentOptions={apartmentOptions.filter((option) => option.label !== "All")}
                />
            </Card>
        </>
    );
};

export default BlockedCalendarPage;
